<template>
    <label
        for="adultsInput"
        class="block font-semibold"
    >
        {{ enrollment.enrollmentType.singular }}
    </label>
    <input-number
        :model-value="enrollment.count"
        show-buttons
        button-layout="horizontal"
        :min="enrollment.minQuantity"
        :max="enrollment.maxQuantity"
        :input-props="{ inputmode: 'numeric' }"
        input-class="w-12 text-center py-2 px-1 border-transparent ring-0 focus:outline-none focus:shadow-none hover:border-transparent dark:focus:shadow-none"
        class="border border-gray-300 focus-within:ring focus-within:ring-offset-1"
        decrement-button-class="order-first"
        :increment-button-props="{ text: true }"
        :decrement-button-props="{ text: true }"
        :pt="{
            root: {
                class: 'w-auto inline-flex'
            }
        }"
        @update:model-value="
            (value: number) => updateCount(value)
        "
    >
        <template #decrementbuttonicon>
            <i class="pi pi-minus" />
        </template>

        <template #incrementbuttonicon>
            <i class="pi pi-plus" />
        </template>
    </input-number>
    <div
        v-if="maximumQuantityReachedMessage !== null"
        class="mt-0.5 text-sm"
    >
        {{ maximumQuantityReachedMessage }}
    </div>
    <div
        v-if="minimumQuantityReachedMessage !== null"
        class="mt-0.5 text-sm"
    >
        {{ minimumQuantityReachedMessage }}
    </div>
</template>

<script setup lang="ts">
import InputNumber from 'primevue/inputnumber'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    enrollment: {
        enrollmentType: {
            id: string
            singular: string
            plural: string
        }
        count: number
        minQuantity: number
        maxQuantity: number
    }
}>()

const emit = defineEmits<{
    (e: 'updateEnrollmentCount', payload: number): void
}>()

const { t } = useI18n()

const showMaximumQuantityMessage = ref(false)
const maximumQuantityReachedMessage = computed(() => {
    if (
        showMaximumQuantityMessage.value &&
        props.enrollment.count === props.enrollment.maxQuantity &&
        props.enrollment.maxQuantity !== props.enrollment.minQuantity
    ) {
        if (props.enrollment.count === 1) {
            return t('purchaseContract.maxEnrollmentType', {
                quantity: props.enrollment.maxQuantity,
                enrollmentTypeName: props.enrollment.enrollmentType.singular
            })
        } else {
            return t('purchaseContract.maxEnrollmentType', {
                quantity: props.enrollment.maxQuantity,
                enrollmentTypeName: props.enrollment.enrollmentType.plural
            })
        }
    }
    return null
})

const minimumQuantityReachedMessage = computed(() => {
    if (
        showMinimumQuantityMessage.value &&
        props.enrollment.minQuantity !== 0 &&
        props.enrollment.maxQuantity !== props.enrollment.minQuantity
    ) {
        if (props.enrollment.count === 1) {
            return t('purchaseContract.minEnrollmentType', {
                quantity: props.enrollment.minQuantity,
                enrollmentTypeName: props.enrollment.enrollmentType.singular
            })
        } else {
            return t('purchaseContract.minEnrollmentType', {
                quantity: props.enrollment.minQuantity,
                enrollmentTypeName: props.enrollment.enrollmentType.plural
            })
        }
    }
    return null
})

const showMinimumQuantityMessage = ref(false)
function updateCount(value: number) {
    if (
        value === props.enrollment.minQuantity &&
        props.enrollment.count === props.enrollment.minQuantity
    ) {
        showMinimumQuantityMessage.value = true
    } else {
        showMinimumQuantityMessage.value = false
    }

    if (
        value === props.enrollment.maxQuantity &&
        props.enrollment.count === props.enrollment.maxQuantity
    ) {
        showMaximumQuantityMessage.value = true
    } else {
        showMaximumQuantityMessage.value = false
    }
    emit('updateEnrollmentCount', value)
}
</script>
